import type { CustomEntryCreateIn, CustomEntryOut, PlanNodeOut } from '@/client';
import { PlanScheduleKinds, PlanStatuses } from '@/components/maintenance/model/planVocabulary';
import { type FrequencySelection, toFrequencySelection } from '@/utils/maintenance/frequencyUtils';

/** A week is always seven days, so weekly cadences are exact as days. Months and years are not. */
const DAYS_PER_WEEK = 7;
const PENDING_CUSTOM_ENTRY_NODE_PREFIX = 'pending_custom_entry:';

/** The composer's task draft, in the fields a custom entry is built from. */
export interface CustomEntryDraft {
	title: string;
	description: string;
	/** ISO yyyy-mm-dd. */
	startDate: string;
	frequency: string;
	isRecurring: boolean;
}

type Recurrence = Pick<CustomEntryCreateIn, 'is_recurring' | 'interval_days' | 'interval_months' | 'interval_years'>;

/**
 * The cadence as exactly one interval field.
 *
 * The API rejects anything else with a 422: a recurring entry must set one of the three and only
 * one, and a one-off must leave all three null. Weeks have no field of their own and go as days —
 * exact, unlike months and years, which the backend counts in calendar units rather than a fixed
 * number of days.
 */
const intervalFor = ({ every, interval }: FrequencySelection): Recurrence => {
	switch (interval) {
		case 'day':
			return { is_recurring: true, interval_days: every };
		case 'week':
			return { is_recurring: true, interval_days: every * DAYS_PER_WEEK };
		case 'month':
			return { is_recurring: true, interval_months: every };
		case 'year':
			return { is_recurring: true, interval_years: every };
	}
};

/**
 * Whether a draft's cadence can be stated in the API's terms.
 *
 * The composer's frequency is free text, and recurrence is fixed at creation — the entry's whole
 * occurrence history keys off it — so a cadence that cannot be expressed must fail the submit gate
 * rather than be guessed at afterwards.
 */
export const isSubmittableCadence = (draft: Pick<CustomEntryDraft, 'frequency' | 'isRecurring'>): boolean =>
	!draft.isRecurring || toFrequencySelection(draft.frequency) !== null;

/**
 * A task draft as the create endpoint takes it.
 *
 * `task_kind` is a free-text grouping hint with nothing in the composer behind it, so it is left
 * unset rather than invented. Callers must gate on `isSubmittableCadence` first; a recurring draft
 * whose cadence does not parse falls back to a one-off here, which is a last resort, not a feature.
 */
export const toCustomEntryPayload = (draft: CustomEntryDraft): CustomEntryCreateIn => {
	const selection = draft.isRecurring ? toFrequencySelection(draft.frequency) : null;

	return {
		label: draft.title.trim(),
		notes: draft.description.trim() || null,
		target_date: draft.startDate,
		...(selection ? intervalFor(selection) : { is_recurring: false }),
	};
};

const intervalLabel = (count: number, singular: string): string =>
	count === 1 ? `Every ${singular}` : `Every ${count} ${singular}s`;

/** The recurrence label available before the plan projection supplies its richer cadence. */
const pendingCadenceText = (entry: CustomEntryOut): string | null => {
	if (!entry.is_recurring) return null;
	if (entry.interval_years)
		return entry.interval_years === 1 ? 'Yearly' : intervalLabel(entry.interval_years, 'year');
	if (entry.interval_months) return intervalLabel(entry.interval_months, 'month');
	if (entry.interval_days) return intervalLabel(entry.interval_days, 'day');
	return null;
};

/** Identifies the inert local row shown while a stored entry becomes an actionable plan card. */
export const isPendingCustomEntryNode = (node: PlanNodeOut): boolean =>
	node.node_id.startsWith(PENDING_CUSTOM_ENTRY_NODE_PREFIX);

/** Returns the durable entry id carried by a local pending-row id. */
export const pendingCustomEntryId = (nodeId: string | undefined): string | null =>
	nodeId?.startsWith(PENDING_CUSTOM_ENTRY_NODE_PREFIX)
		? nodeId.slice(PENDING_CUSTOM_ENTRY_NODE_PREFIX.length) || null
		: null;

/**
 * The durable create response as a non-actionable schedule row.
 *
 * The endpoint explicitly returns an input rather than a plan card. This local node therefore
 * carries only fields the create response can state truthfully and no resolve token or actions.
 * It is replaced, never promoted, once the plan emits the authoritative card.
 */
export const pendingCustomEntryNode = (entry: CustomEntryOut): PlanNodeOut => {
	const cadenceText = pendingCadenceText(entry);
	return {
		node_id: `${PENDING_CUSTOM_ENTRY_NODE_PREFIX}${entry.id}`,
		node_type: 'leaf',
		kind: 'custom',
		category: 'maintenance',
		source_origin: 'user',
		title: entry.label,
		description: entry.notes ?? null,
		next_due: entry.target_date ?? null,
		is_provisional: false,
		action_target: {
			target_date: entry.target_date ?? null,
			write_anchor: { kind: 'custom_entry', custom_entry_id: entry.id },
			resolve_token: null,
		},
		status: PlanStatuses.Upcoming,
		allowed_actions: [],
		children: [],
		child_count: 0,
		display_category: 'Routine',
		schedule: {
			kind: entry.is_recurring ? PlanScheduleKinds.Recurring : PlanScheduleKinds.OneOff,
			is_recurring: entry.is_recurring,
			timing_label: entry.is_recurring ? null : 'One time',
			cadence: cadenceText ? { text: cadenceText, is_inferred: false } : {},
		},
	};
};
